import React, { useMemo } from 'react';
import { MEDIA_VARIANTS } from '../../../media';
import { SelectionPickerField } from './ResourceSpawnRulesEditor';

function value(source, camel, pascal) {
  return source?.[camel] ?? source?.[pascal];
}

function tileOptions(tiles, group) {
  return (tiles || []).map(tile => {
    const key = value(tile, 'key', 'Key')
      || value(tile, 'id', 'Id');
    return {
      value: key,
      label: value(tile, 'label', 'Label')
        || value(tile, 'name', 'Name')
        || key,
      group,
      keywords: [key],
    };
  }).filter(option => option.value && option.value !== 'none');
}

function LootContainerRulesEditor({
  baseTiles,
  containerTypes,
  detailTiles,
  getImageUrl,
  onAddPlacement,
  onAddRule,
  onChangePlacement,
  onChangeRule,
  onPlace,
  onPreview,
  onRemovePlacement,
  onRemoveRule,
  placementMode,
  placements,
  preview,
  previewBusy,
  previewError,
  rules,
}) {
  const types = useMemo(() => new Map(containerTypes.map(type => [
    String(value(type, 'id', 'Id') || ''),
    type,
  ])), [containerTypes]);
  const baseOptions = useMemo(
    () => tileOptions(baseTiles, 'Base tiles'),
    [baseTiles],
  );
  const detailOptions = useMemo(
    () => tileOptions(detailTiles, 'Details'),
    [detailTiles],
  );
  const previewErrors = value(preview, 'errors', 'Errors') || [];
  const previewPlacements = value(
    preview,
    'effectivePlacements',
    'EffectivePlacements',
  ) || [];
  const previewRules = value(preview, 'rules', 'Rules') || [];

  return (
    <section className="world-editor-section" aria-labelledby="loot-rules-title">
      <div className="world-editor-section__heading">
        <div>
          <h3 id="loot-rules-title">Authored Loot</h3>
          <p>
            Place fixed containers or keep an active population in selected
            habitats. Rule containers relocate only after they are emptied and
            finish refilling.
          </p>
        </div>
      </div>

      <div className="world-editor-section__heading">
        <h4>Manual containers</h4>
        <button
          className="button-secondary"
          disabled={containerTypes.length === 0}
          type="button"
          onClick={onAddPlacement}
        >
          Add Container
        </button>
      </div>
      {placements.length === 0 && (
        <p className="world-editor-empty">No fixed loot containers.</p>
      )}
      <div className="world-editor-card-list">
        {placements.map((placement, index) => {
          const prefix = `loot-placement-${index}`;
          const type = types.get(placement.containerTypeId);
          const imageId = value(type, 'closedImageId', 'ClosedImageId');
          const imageUrl = imageId
            ? getImageUrl(imageId, MEDIA_VARIANTS.ENTITY_ICON)
            : null;
          return (
            <div className="world-editor-card" key={placement.placementId}>
              <div className="world-editor-card__body">
                <label className="form-control" htmlFor={`${prefix}-type`}>
                  <span>Container type</span>
                  <select
                    id={`${prefix}-type`}
                    value={placement.containerTypeId}
                    onChange={event => onChangePlacement(index, {
                      containerTypeId: event.target.value,
                    })}
                  >
                    {containerTypes.map(option => (
                      <option
                        key={value(option, 'id', 'Id')}
                        value={value(option, 'id', 'Id')}
                      >
                        {value(option, 'name', 'Name')}
                      </option>
                    ))}
                  </select>
                </label>
                {imageUrl && (
                  <span
                    aria-label={`${value(type, 'name', 'Name')} preview`}
                    className="worldeditor-resource-entity worldeditor-resource-entity--image"
                    role="img"
                    style={{
                      backgroundImage: `url(${imageUrl})`,
                      position: 'static',
                      transform: 'none',
                    }}
                  />
                )}
                <p>
                  Position: {placement.x}, {placement.y}
                  {' · '}ID: {placement.placementId}
                </p>
                <div className="world-editor-card__actions">
                  <button
                    className={`button-secondary ${
                      placementMode === `loot:${placement.placementId}`
                        ? 'is-active'
                        : ''
                    }`}
                    type="button"
                    onClick={() => onPlace(placement.placementId)}
                  >
                    {placementMode === `loot:${placement.placementId}`
                      ? 'Click a tile…'
                      : 'Place on Map'}
                  </button>
                  <button
                    className="button-critical"
                    type="button"
                    onClick={() => onRemovePlacement(index)}
                  >
                    Remove
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="world-editor-section__heading">
        <h4>Spawn rules</h4>
        <button
          className="button-secondary"
          disabled={containerTypes.length === 0}
          type="button"
          onClick={onAddRule}
        >
          Add Rule
        </button>
      </div>
      {rules.length === 0 && (
        <p className="world-editor-empty">No loot habitat rules.</p>
      )}
      <div className="world-editor-card-list">
        {rules.map((rule, index) => {
          const prefix = `loot-rule-${index}`;
          const near = rule.near || {
            baseKeys: [],
            detailKeysAny: [],
            maxManhattanDistance: 1,
          };
          const updateNear = patch => onChangeRule(index, {
            near: { ...near, ...patch },
          });
          return (
            <details className="world-editor-card" key={`${rule.key}-${index}`}>
              <summary>
                <span>{rule.key || `Rule ${index + 1}`}</span>
                <span className={rule.enabled ? 'status-enabled' : 'status-disabled'}>
                  {rule.enabled ? `${rule.count} active` : 'Disabled'}
                </span>
              </summary>
              <div className="world-editor-card__body">
                <label className="form-control" htmlFor={`${prefix}-key`}>
                  <span>Stable rule key</span>
                  <input
                    id={`${prefix}-key`}
                    value={rule.key}
                    onChange={event => onChangeRule(index, {
                      key: event.target.value,
                    })}
                  />
                </label>
                <label className="form-control" htmlFor={`${prefix}-type`}>
                  <span>Container type</span>
                  <select
                    id={`${prefix}-type`}
                    value={rule.containerTypeId}
                    onChange={event => onChangeRule(index, {
                      containerTypeId: event.target.value,
                    })}
                  >
                    {containerTypes.map(type => (
                      <option
                        key={value(type, 'id', 'Id')}
                        value={value(type, 'id', 'Id')}
                      >
                        {value(type, 'name', 'Name')}
                      </option>
                    ))}
                  </select>
                </label>
                <div className="world-editor-number-grid">
                  <label className="form-control" htmlFor={`${prefix}-count`}>
                    <span>Active count</span>
                    <input
                      id={`${prefix}-count`}
                      min="1"
                      type="number"
                      value={rule.count}
                      onChange={event => onChangeRule(index, {
                        count: Math.max(1, Number(event.target.value) || 1),
                      })}
                    />
                  </label>
                  <label className="form-control" htmlFor={`${prefix}-spacing`}>
                    <span>Minimum spacing</span>
                    <input
                      id={`${prefix}-spacing`}
                      min="0"
                      type="number"
                      value={rule.minimumSpacing}
                      onChange={event => onChangeRule(index, {
                        minimumSpacing: Math.max(
                          0,
                          Number(event.target.value) || 0,
                        ),
                      })}
                    />
                  </label>
                  <label className="form-control" htmlFor={`${prefix}-radius`}>
                    <span>Spawn exclusion radius</span>
                    <input
                      id={`${prefix}-radius`}
                      min="0"
                      type="number"
                      value={rule.excludeSpawnRadius}
                      onChange={event => onChangeRule(index, {
                        excludeSpawnRadius: Math.max(
                          0,
                          Number(event.target.value) || 0,
                        ),
                      })}
                    />
                  </label>
                </div>
                <SelectionPickerField
                  addLabel="Add allowed base tiles"
                  description="The container may occupy any selected base tile."
                  label="Allowed base tiles (any)"
                  onChange={values => onChangeRule(index, {
                    allowedBaseKeys: values,
                  })}
                  options={baseOptions}
                  title={`Allowed base tiles for ${rule.key}`}
                  values={rule.allowedBaseKeys}
                />
                <SelectionPickerField
                  addLabel="Add required details"
                  description="The spawn tile must use one of these details."
                  label="Required details (any)"
                  onChange={values => onChangeRule(index, {
                    allowedDetailKeysAny: values,
                  })}
                  optionalLabel="Require details on the spawn tile"
                  options={detailOptions}
                  title={`Required details for ${rule.key}`}
                  toggleId={`${prefix}-details-enabled`}
                  values={rule.allowedDetailKeysAny}
                />
                <label className="world-editor-checkbox" htmlFor={`${prefix}-near`}>
                  <input
                    checked={Boolean(rule.near)}
                    id={`${prefix}-near`}
                    type="checkbox"
                    onChange={event => onChangeRule(index, {
                      near: event.target.checked ? near : null,
                    })}
                  />
                  <span>Require nearby terrain or details</span>
                </label>
                {rule.near && (
                  <>
                    <SelectionPickerField
                      addLabel="Add nearby base tiles"
                      label="Nearby base tiles (any)"
                      onChange={values => updateNear({ baseKeys: values })}
                      options={baseOptions}
                      title={`Nearby base tiles for ${rule.key}`}
                      values={near.baseKeys}
                    />
                    <SelectionPickerField
                      addLabel="Add nearby details"
                      label="Nearby details (any)"
                      onChange={values => updateNear({
                        detailKeysAny: values,
                      })}
                      options={detailOptions}
                      title={`Nearby details for ${rule.key}`}
                      values={near.detailKeysAny}
                    />
                    <label className="form-control" htmlFor={`${prefix}-near-distance`}>
                      <span>Nearby distance</span>
                      <input
                        id={`${prefix}-near-distance`}
                        min="1"
                        type="number"
                        value={near.maxManhattanDistance}
                        onChange={event => updateNear({
                          maxManhattanDistance: Math.max(
                            1,
                            Number(event.target.value) || 1,
                          ),
                        })}
                      />
                    </label>
                  </>
                )}
                <label className="world-editor-checkbox" htmlFor={`${prefix}-enabled`}>
                  <input
                    checked={rule.enabled}
                    id={`${prefix}-enabled`}
                    type="checkbox"
                    onChange={event => onChangeRule(index, {
                      enabled: event.target.checked,
                    })}
                  />
                  <span>Use this rule</span>
                </label>
                <div className="world-editor-card__actions">
                  <button
                    className="button-critical"
                    type="button"
                    onClick={() => onRemoveRule(index)}
                  >
                    Remove
                  </button>
                </div>
              </div>
            </details>
          );
        })}
      </div>

      <button
        className="button-secondary"
        disabled={previewBusy}
        type="button"
        onClick={onPreview}
      >
        {previewBusy ? 'Calculating Preview…' : 'Preview Loot on Map'}
      </button>
      {previewError && (
        <p className="world-editor-feedback world-editor-feedback--error" role="alert">
          {previewError}
        </p>
      )}
      {preview && (
        <div
          className={`resource-preview-summary resource-preview-summary--${
            previewErrors.length === 0 ? 'valid' : 'invalid'
          }`}
          role={previewErrors.length === 0 ? 'status' : 'alert'}
        >
          <strong>
            {previewErrors.length === 0
              ? `${previewPlacements.length} resolved loot containers`
              : `${previewErrors.length} validation issues`}
          </strong>
          {previewErrors.length > 0 && (
            <ul>
              {previewErrors.map((error, index) => (
                <li key={`${value(error, 'path', 'Path')}-${index}`}>
                  {value(error, 'message', 'Message')}
                </li>
              ))}
            </ul>
          )}
          {previewRules.length > 0 && (
            <ul>
              {previewRules.map(result => (
                <li key={value(result, 'ruleKey', 'RuleKey')}>
                  {value(result, 'ruleKey', 'RuleKey')}: {' '}
                  {value(result, 'selectedCount', 'SelectedCount')} selected
                  from {` ${value(result, 'eligibleCount', 'EligibleCount')} `}
                  eligible tiles
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}

export default LootContainerRulesEditor;
